// src/pages/PackagingPage.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom'; // Importa ReactDOM para usar Portals

import { Tarjeta } from '../components/Tarjeta'; // Para las tarjetas de ejemplo (si se usan)
import { listaPackagings as rawInitialListaPackagings } from '../data/packagings'; // Datos iniciales
import { TarjetaPackaging, obtenerEstadoChecklist } from '../components/TarjetaPackaging'; // Componente de la tarjeta de packaging
import { Boton } from '../Boton'; // Tu componente Boton

// Datos de ejemplo para Tarjeta (corregido para usar 'id' único)
const tarjetasData = [
  { id: 'ejemplo1', titulo: 'Tarjeta 1', descripcion: 'Descripción de la tarjeta 1' },
  { id: 'ejemplo2', titulo: 'Tarjeta 2', descripcion: 'Descripción de la tarjeta 2' },
];

// Función para generar IDs únicos (más robusta)
const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Define la estructura base de una checklist para nuevos packagings
const baseChecklistItems = [
  { etiqueta: "Pedido solicitado", validado: false, fecha: "" },
  { etiqueta: "Regulatory revisado", validado: false, fecha: "" },
  { etiqueta: "Cotizado por compras", validado: false, fecha: "" },
  { etiqueta: "Diseño revisado", validado: false, fecha: "" },
  { etiqueta: "Proveedor validado", validado: false, fecha: "" },
  { etiqueta: "Pedido compras validado", validado: false, fecha: "" }
];

// Procesa los datos iniciales de packagings para asegurar que todos tengan un ID único
// y una checklist completa (aunque no validada)
const processedInitialPackagings = rawInitialListaPackagings.map(pkg => ({
  ...pkg,
  id: pkg.id || generateUniqueId(), // Asigna un ID si no existe
  // Asegura que la checklist exista y tenga la estructura base, si no la tiene
  checklist: pkg.checklist && pkg.checklist.length > 0 ? pkg.checklist : baseChecklistItems.map(item => ({ ...item }))
}));


// PackagingPage ahora recibe 'modalRoot' como una prop
function PackagingPage({ modalRoot }) {
  // --- Estados ---
  const [packagings, setPackagings] = useState(processedInitialPackagings); // Lista de packagings
  const [packagingSeleccionado, setPackagingSeleccionado] = useState(null); // Packaging para ver/editar en modal
  const [newPackaging, setNewPackaging] = useState({ // Estado para el formulario de añadir
    id: '', nombreComercial: '', codigoAS400: '', descripcion: '', tipo: '', formato: '',
    material: '', color: '', medidas: { alto: '', ancho: '', largo: '', proveedor: ''},
    checklist: baseChecklistItems.map(item => ({ ...item })), // Inicializa con la checklist base
  });
  const [isEditing, setIsEditing] = useState(false); // Modo edición en el modal de detalles
  const [isAddModalOpen, setIsAddModalOpen] = useState(false); // Visibilidad del modal de añadir

  // modalRoot ya se recibe por prop, no necesitamos el estado local ni useEffect aquí
  // Comprobación si modalRoot existe (para el render condicional del portal)
  const isModalRootAvailable = modalRoot !== null;


  // Determina si algún modal está abierto para aplicar el blur al fondo
  const isAnyModalOpen = packagingSeleccionado !== null || isAddModalOpen;


  // --- Funciones CRUD ---

  const handleAddPackaging = (e) => {
    e.preventDefault();
    const id = generateUniqueId(); // Genera un ID robusto
    const packagingToAdd = { ...newPackaging, id, medidas: { ...newPackaging.medidas } };

    setPackagings((prevPackagings) => [...prevPackagings, packagingToAdd]);
    setNewPackaging({ // Limpia el formulario y reestablece checklist
      id: '', nombreComercial: '', codigoAS400: '', descripcion: '', tipo: '', formato: '',
      material: '', color: '', medidas: { alto: '', ancho: '', largo: '', proveedor: ''},
      checklist: baseChecklistItems.map(item => ({ ...item })), // Reestablecer checklist
    });
    closeAddModal();
  };

  const handleNewPackagingChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('medidas.')) {
      const medidaKey = name.split('.')[1];
      setNewPackaging((prev) => ({
        ...prev,
        medidas: {
          ...prev.medidas,
          [medidaKey]: value,
        },
      }));
    } else {
      setNewPackaging((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdatePackaging = (e) => {
    e.preventDefault();
    if (packagingSeleccionado && packagingSeleccionado.id) {
      setPackagings((prevPackagings) =>
        prevPackagings.map((pkg) =>
          pkg.id === packagingSeleccionado.id ? { ...packagingSeleccionado, medidas: { ...packagingSeleccionado.medidas } } : pkg
        )
      );
      setIsEditing(false);
      setPackagingSeleccionado(null);
    } else {
      console.error("Error: No se puede actualizar. Packaging seleccionado o su ID es inválido.");
    }
  };

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('medidas.')) {
      const medidaKey = name.split('.')[1];
      setPackagingSeleccionado((prev) => ({
        ...prev,
        medidas: {
          ...prev.medidas,
          [medidaKey]: value,
        },
      }));
    } else {
      setPackagingSeleccionado((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Manejar el cambio de estado de un ítem de la checklist (para el modal de edición)
  const handleChecklistItemToggle = (itemIndex) => {
    setPackagingSeleccionado((prevPackaging) => {
      if (!prevPackaging || !prevPackaging.checklist) return prevPackaging;

      const updatedChecklist = prevPackaging.checklist.map((item, idx) => {
        if (idx === itemIndex) {
          const newValidatedState = !item.validado;
          // Formato de fecha AAAA-MM-DD
          const newDate = newValidatedState ? new Date().toISOString().split('T')[0] : "";
          return { ...item, validado: newValidatedState, fecha: newDate };
        }
        return item;
      });
      return { ...prevPackaging, checklist: updatedChecklist };
    });
  };


  const handleDeletePackaging = () => {
    if (packagingSeleccionado && packagingSeleccionado.id) {
      if (window.confirm(`¿Estás seguro de que quieres eliminar "${packagingSeleccionado.nombreComercial}"?`)) {
        setPackagings((prevPackagings) =>
          prevPackagings.filter((pkg) => pkg.id !== packagingSeleccionado.id)
        );
        setPackagingSeleccionado(null);
      }
    } else {
      console.error("Error: No se puede eliminar. Packaging seleccionado o su ID es inválido.");
      setPackagingSeleccionado(null);
    }
  };

  const openModalForView = (pkg) => {
    setPackagingSeleccionado(pkg);
    setIsEditing(false);
  };

  const closeModal = () => {
    setPackagingSeleccionado(null);
    setIsEditing(false);
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setNewPackaging({
      id: '', nombreComercial: '', codigoAS400: '', descripcion: '', tipo: '', formato: '',
      material: '', color: '', medidas: { alto: '', ancho: '', largo: '' , proveedor: ''},
      checklist: baseChecklistItems.map(item => ({ ...item })), // Reestablecer checklist
    });
  };

  return (
    <div className={`page-content ${isAnyModalOpen ? 'blur' : ''}`}>

      <div style={{ marginBottom: '2rem' }}>
        <Boton
          tipo="primario"
          texto="Añadir Nuevo Packaging"
          size="normal"
          onClick={openAddModal}
        />
      </div>

      <div className="tarjetas-grid">
        {packagings.map((pkg) => (
          <TarjetaPackaging
            key={pkg.id}
            packaging={pkg}
            onClick={() => openModalForView(pkg)}
          />
        ))}
      </div>

      <div className="tarjetas-grid">
        {tarjetasData.map((tarjeta) => (
          <Tarjeta
            key={tarjeta.id}
            titulo={tarjeta.titulo}
            descripcion={tarjeta.descripcion}
          />
        ))}
      </div>


      {/* MODAL DE AÑADIR NUEVO PACKAGING - ¡USANDO modalRoot de las props! */}
      {isAddModalOpen && isModalRootAvailable && ReactDOM.createPortal( // Aseguramos que modalRoot exista
        <div className={`modal-overlay ${isAddModalOpen ? 'open' : ''}`} onClick={closeAddModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Añadir Nuevo Packaging</h2>
            <form onSubmit={handleAddPackaging} className="formulario-packaging">
              <label> Nombre Comercial: <input type="text" name="nombreComercial" value={newPackaging.nombreComercial} onChange={handleNewPackagingChange} className="input-largo" required /> </label>
              <label> Código AS400: <input type="text" name="codigoAS400" value={newPackaging.codigoAS400} onChange={handleNewPackagingChange} className="input-corto" required /> </label>
              <label style={{ gridColumn: '1 / -1' }}> Descripción: <textarea name="descripcion" value={newPackaging.descripcion} onChange={handleNewPackagingChange} className="input-largo" rows="3" ></textarea> </label>
              <label> Tipo: <input type="text" name="tipo" value={newPackaging.tipo} onChange={handleNewPackagingChange} className="input-medio" /> </label>
              <label> Formato: <input type="text" name="formato" value={newPackaging.formato} onChange={handleNewPackagingChange} className="input-medio" /> </label>
              <label> Material: <input type="text" name="material" value={newPackaging.material} onChange={handleNewPackagingChange} className="input-medio" /> </label>
              <label> Color: <input type="text" name="color" value={newPackaging.color} onChange={handleNewPackagingChange} className="input-medio" /> </label>
              <label> Medidas (Alto): <input type="number" name="medidas.alto" value={newPackaging.medidas.alto} onChange={handleNewPackagingChange} className="input-corto" /> </label>
              <label> Medidas (Ancho): <input type="number" name="medidas.ancho" value={newPackaging.medidas.ancho} onChange={handleNewPackagingChange} className="input-corto" /> </label>
              <label> Medidas (Largo): <input type="number" name="medidas.largo" value={newPackaging.medidas.largo} onChange={handleNewPackagingChange} className="input-corto" /> </label>

              <Boton type="submit" tipo="primario" texto="Añadir Packaging" size="normal" style={{ gridColumn: '1 / -1', justifySelf: 'center', marginTop: '1.5rem' }} />
              <Boton type="button" tipo="secundario" texto="Cancelar" size="normal" onClick={closeAddModal} style={{ gridColumn: '1 / -1', justifySelf: 'center', marginTop: '0.5rem' }} />
            </form>
          </div>
        </div>,
        modalRoot // Usamos la referencia de las props
      )}


      {/* MODAL DE DETALLE/EDICIÓN - ¡USANDO modalRoot de las props! */}
      {packagingSeleccionado && isModalRootAvailable && ReactDOM.createPortal( // Aseguramos que modalRoot exista
        <div className={`modal-overlay ${isAnyModalOpen ? 'open' : ''}`} onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {isEditing ? (
              <form onSubmit={handleUpdatePackaging} className="formulario-packaging">
                <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Editar Packaging</h2>
                <label> Nombre Comercial: <input type="text" name="nombreComercial" value={packagingSeleccionado.nombreComercial} onChange={handleModalChange} className="input-largo" required /> </label>
                <label> Código AS400: <input type="text" name="codigoAS400" value={packagingSeleccionado.codigoAS400} onChange={handleModalChange} className="input-corto" required /> </label>
                <label style={{ gridColumn: '1 / -1' }}> Descripción: <textarea name="descripcion" value={packagingSeleccionado.descripcion} onChange={handleModalChange} className="input-largo" rows="3" ></textarea> </label>
                <label> Tipo: <input type="text" name="tipo" value={packagingSeleccionado.tipo} onChange={handleModalChange} className="input-medio" /> </label>
                <label> Formato: <input type="text" name="formato" value={packagingSeleccionado.formato} onChange={handleModalChange} className="input-medio" /> </label>
                <label> Material: <input type="text" name="material" value={packagingSeleccionado.material} onChange={handleModalChange} className="input-medio" /> </label>
                <label> Color: <input type="text" name="color" value={packagingSeleccionado.color} onChange={handleModalChange} className="input-medio" /> </label>
                <label> Medidas (Alto): <input type="number" name="medidas.alto" value={packagingSeleccionado.medidas.alto} onChange={handleModalChange} className="input-corto" /> </label>
                <label> Medidas (Ancho): <input type="number" name="medidas.ancho" value={packagingSeleccionado.medidas.ancho} onChange={handleModalChange} className="input-corto" /> </label>
                <label> Medidas (Largo): <input type="number" name="medidas.largo" value={packagingSeleccionado.medidas.largo} onChange={handleModalChange} className="input-corto" /> </label>
                <label> Proveedor: <input type="text" name="proveedor" value={packagingSeleccionado.proveedor} onChange={handleModalChange} className="input-largo"/> </label>


                {Array.isArray(packagingSeleccionado.checklist) && (
                  <div
                    className="checklist"
                    style={{
                      gridColumn: '1 / -1',
                      border: '1px solid #4a5a70',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginTop: '1.5rem',
                    }}
                  >
                    <h3
                      style={{
                        marginTop: '0',
                        color: '#32d4fa',
                      }}
                    >
                      Checklist de Validación
                    </h3>

                    {packagingSeleccionado.checklist.map((item, idx) => {
                      const fechaValida = item.fecha && !isNaN(new Date(item.fecha));

                      return (
                        <div key={idx} className="check-item" style={{ marginBottom: '0.7rem' }}>
                          <label
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.validado}
                              onChange={() => handleChecklistItemToggle(idx)}
                              style={{ marginRight: '0.5rem', transform: 'scale(1.2)' }}
                            />
                            <span>{item.etiqueta}</span>

                            <span
                              className={`check-date ${!fechaValida ? 'pendiente' : ''}`}
                            >   &nbsp;
                              {fechaValida
                                ? new Intl.DateTimeFormat('es-ES', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }).format(new Date(item.fecha))
                                : 'Pendiente'}

                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}


                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
                  <Boton type="submit" tipo="primario" texto="Guardar Cambios" size="normal" />
                  <Boton type="button" tipo="peligro" texto="Eliminar Packaging" size="normal" onClick={handleDeletePackaging} />
                </div>
                <Boton type="button" tipo="secundario" texto="Cancelar" size="normal" onClick={closeModal} style={{ gridColumn: '1 / -1', justifySelf: 'center', marginTop: '1rem' }} />
              </form>
            ) : (
              <>
                <div className={`estado-tarjeta ${obtenerEstadoChecklist(packagingSeleccionado.checklist).clase}`}>
                  {obtenerEstadoChecklist(packagingSeleccionado.checklist).texto}
                </div>
                <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>{packagingSeleccionado.nombreComercial}</h2>
                <p><strong>Código:</strong> {packagingSeleccionado.codigoAS400}</p>
                <p><strong>Descripción:</strong> {packagingSeleccionado.descripcion}</p>
                <p><strong>Tipo:</strong> {packagingSeleccionado.tipo}</p>
                <p><strong>Formato:</strong> {packagingSeleccionado.formato}</p>
                <p><strong>Material:</strong> {packagingSeleccionado.material}</p>
                <p><strong>Color:</strong> {packagingSeleccionado.color}</p>
                <p><strong>Medidas:</strong> {packagingSeleccionado.medidas.alto}x{packagingSeleccionado.medidas.ancho}x{packagingSeleccionado.medidas.largo} cm</p>
                <p><strong>Proveedor:</strong> {packagingSeleccionado.proveedor}</p>

                {Array.isArray(packagingSeleccionado.checklist) && (
                  <div className="checklist">
                    {packagingSeleccionado.checklist.map((item, idx) => {
                      const fechaValida = item.fecha && !isNaN(new Date(item.fecha));
                      return (
                        <div key={idx} className="check-item">
                          <div className={`check-circle ${item.validado ? 'completo' : ''}`}></div>
                          <span>{item.etiqueta} -&nbsp;</span>
                          <span
                            className={`check-date ${!fechaValida ? 'pendiente' : ''}`}>
                            {fechaValida
                              ? new Intl.DateTimeFormat('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }).format(new Date(item.fecha))
                              : 'Pendiente'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '1.5rem' }}>
                  <Boton type="button" tipo="primario" texto="Editar" size="normal" onClick={() => setIsEditing(true)} />
                  <Boton type="button" tipo="secundario" texto="Cerrar" size="normal" onClick={closeModal} />
                </div>
              </>
            )}
          </div>
        </div>,
        modalRoot // Usamos la referencia de las props
      )}
    </div>
  );
}

export default PackagingPage;